/**
 * Rhythmz — AI DJ Agent
 * ──────────────────────
 * Manages a full AI-generated setlist with:
 * - Track queue (genre, key, scale, bpm, bars, transition type)
 * - Cut-in/cut-out (abrupt change on phrase boundary)
 * - Mix transitions (gradual BPM ramp + genre blend over 4 bars)
 * - Filler music (ambient pad between high-energy tracks for energy recovery)
 * - Visual sync (scene switches on track changes, intensity follows energy)
 *
 * Uses ComposerEngine for audio and VisualDJAIAgent for visual sync.
 */

import type { ComposerEngine } from './ComposerEngine';
import type { AIAgentCoordinator } from './AIAgentCoordinator';
import { aiClient } from './client';

export interface DJTrack {
  id: string;
  genre: string;
  key: string;
  scale: string;
  bpm: number;
  bars: number;
  transition: 'cut' | 'mix' | 'filler';
  energy: number; // 0-1, how intense this track is
  name: string;
}

export interface DJSetlist {
  tracks: DJTrack[];
  vibe: string;
  totalTracks: number;
  estimatedMinutes: number;
}

export interface DJCallbacks {
  onTrackChange: (track: DJTrack, index: number) => void;
  onTransition: (type: string, fromTrack: DJTrack, toTrack: DJTrack) => void;
  onProgress: (msg: string) => void;
  onSetlistGenerated: (setlist: DJSetlist) => void;
}

const GENRES = ['techno', 'house', 'ambient', 'drumnbass', 'dubstep', 'trance', 'hiphop', 'cinematic', 'loFi', 'synthwave', 'industrial', 'experimental'];
const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const SCALES = ['minor', 'major', 'dorian', 'phrygian', 'lydian', 'mixolydian', 'blues', 'pentatonicMinor'];

export class DJAgent {
  private composer: ComposerEngine | null = null;
  private coordinator: AIAgentCoordinator;
  private callbacks: DJCallbacks;
  private setlist: DJSetlist | null = null;
  private currentTrackIdx = -1;
  private running = false;
  private trackTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    coordinator: AIAgentCoordinator,
    callbacks: DJCallbacks,
  ) {
    this.coordinator = coordinator;
    this.callbacks = callbacks;
  }

  setComposer(composer: ComposerEngine): void {
    this.composer = composer;
  }

  /** Parse raw track objects into validated DJTrack[]. */
  private parseTracks(raw: Record<string, unknown>[]): DJTrack[] {
    return raw.slice(0, 8).map((t, i) => ({
      id: `track-${i}-${Date.now()}`,
      genre: String(t.genre ?? 'techno').toLowerCase(),
      key: String(t.key ?? 'A'),
      scale: String(t.scale ?? 'minor'),
      bpm: Math.max(60, Math.min(200, Number(t.bpm ?? 128))),
      bars: Math.max(4, Math.min(64, Number(t.bars ?? 32))),
      transition: String(t.transition ?? 'cut').toLowerCase() as 'cut' | 'mix' | 'filler',
      energy: Math.max(0, Math.min(1, Number(t.energy ?? 0.5))),
      name: String(t.name ?? t.title ?? `Track ${i + 1}`),
    }));
  }

  /** Generate a setlist from a vibe description using edge AI. */
  async generateSetlist(vibe: string): Promise<DJSetlist> {
    this.callbacks.onProgress(`🤖 AI DJ crafting setlist for: "${vibe.slice(0, 50)}..."`);

    try {
      const result = await aiClient.generateText(
        `You are a 2026 AI DJ. Create a 6-track setlist for this vibe: "${vibe}".
Respond with ONLY JSON (no markdown):
{
  "tracks": [
    {"genre": "techno", "bpm": 128, "key": "C", "scale": "minor", "bars": 32, "transition": "cut", "energy": 0.8, "name": "Track 1"},
    ...6 tracks total...
  ]
}
Rules:
- Start with lower energy, build to peak, then cool down
- Use "cut" for abrupt transitions, "mix" for gradual blends, "filler" for ambient recovery
- Vary genres but keep them cohesive
- BPM should be compatible between consecutive tracks (within 20 bpm for mix transitions)
- Available genres: ${GENRES.join(', ')}
- Available keys: ${KEYS.join(', ')}
- Available scales: ${SCALES.join(', ')}`,
        { system: 'You are an AI DJ. Return only JSON.', maxTokens: 600, temperature: 0.8 },
      );

      if (result.success && result.result) {
        const text = result.result;
        let tracks: DJTrack[] = [];

        // Attempt 1: parse as full JSON object with tracks array
        const fullMatch = text.match(/\{[\s\S]*\}/);
        if (fullMatch) {
          try {
            const parsed = JSON.parse(fullMatch[0]);
            if (parsed.tracks && Array.isArray(parsed.tracks)) {
              tracks = this.parseTracks(parsed.tracks);
            }
          } catch { /* fall through to attempt 2 */ }
        }

        // Attempt 2: extract individual track objects (1B model often returns objects without array)
        if (tracks.length === 0) {
          const objMatches = text.match(/\{[^}]*"genre"[^}]*\}/g);
          if (objMatches && objMatches.length > 0) {
            const rawTracks = objMatches.map((o) => {
              try { return JSON.parse(o); } catch { return null; }
            }).filter((t) => t !== null);
            if (rawTracks.length > 0) {
              tracks = this.parseTracks(rawTracks);
            }
          }
        }

        if (tracks.length > 0) {
          const totalBars = tracks.reduce((s, t) => s + t.bars, 0);
          const avgBpm = tracks.reduce((s, t) => s + t.bpm, 0) / tracks.length;
          const estimatedMinutes = Math.round((totalBars * 4) / avgBpm);

          this.setlist = { tracks, vibe, totalTracks: tracks.length, estimatedMinutes };
          this.callbacks.onSetlistGenerated(this.setlist);
          this.callbacks.onProgress(`✅ Setlist generated: ${tracks.length} tracks, ~${estimatedMinutes}min`);
          return this.setlist;
        }
      }
    } catch (e) {
      this.callbacks.onProgress(`⚠️ AI setlist failed, using fallback: ${e instanceof Error ? e.message : String(e)}`);
    }

    // Fallback: generate a basic setlist
    return this.generateFallbackSetlist(vibe);
  }

  /** Generate a simple fallback setlist without AI. */
  private generateFallbackSetlist(vibe: string): DJSetlist {
    const tracks: DJTrack[] = [
      { id: 't1', genre: 'ambient', key: 'A', scale: 'minor', bpm: 90, bars: 16, transition: 'cut', energy: 0.2, name: 'Intro' },
      { id: 't2', genre: 'house', key: 'A', scale: 'minor', bpm: 124, bars: 32, transition: 'mix', energy: 0.5, name: 'Build Up' },
      { id: 't3', genre: 'techno', key: 'A', scale: 'minor', bpm: 130, bars: 32, transition: 'cut', energy: 0.8, name: 'Peak' },
      { id: 't4', genre: 'dubstep', key: 'A', scale: 'minor', bpm: 140, bars: 32, transition: 'cut', energy: 1.0, name: 'Drop' },
      { id: 't5', genre: 'ambient', key: 'A', scale: 'minor', bpm: 90, bars: 16, transition: 'filler', energy: 0.2, name: 'Recovery' },
      { id: 't6', genre: 'trance', key: 'A', scale: 'minor', bpm: 138, bars: 32, transition: 'mix', energy: 0.7, name: 'Finale' },
    ];
    this.setlist = { tracks, vibe, totalTracks: tracks.length, estimatedMinutes: 20 };
    this.callbacks.onSetlistGenerated(this.setlist);
    this.callbacks.onProgress(`✅ Fallback setlist: ${tracks.length} tracks`);
    return this.setlist;
  }

  /** Start playing the setlist from the beginning. */
  async play(): Promise<void> {
    if (!this.setlist || this.setlist.tracks.length === 0) {
      this.callbacks.onProgress('⚠️ No setlist loaded. Generate one first.');
      return;
    }
    this.running = true;
    this.currentTrackIdx = -1;
    await this.nextTrack();
  }

  /** Stop the setlist. */
  stop(): void {
    this.running = false;
    if (this.trackTimer) {
      clearTimeout(this.trackTimer);
      this.trackTimer = null;
    }
    this.composer?.stop();
    this.callbacks.onProgress('⏹ DJ setlist stopped');
  }

  /** Advance to the next track in the setlist. */
  private async nextTrack(): Promise<void> {
    if (!this.running || !this.setlist) return;

    this.currentTrackIdx++;
    if (this.currentTrackIdx >= this.setlist.tracks.length) {
      // Loop back to start
      this.currentTrackIdx = 0;
      this.callbacks.onProgress('🔄 Setlist complete — looping');
    }

    const track = this.setlist.tracks[this.currentTrackIdx];
    const prevTrack = this.currentTrackIdx > 0 ? this.setlist.tracks[this.currentTrackIdx - 1] : null;

    // Handle transition
    if (prevTrack && track.transition) {
      await this.handleTransition(track.transition, prevTrack, track);
    }

    // Play the track
    this.callbacks.onTrackChange(track, this.currentTrackIdx);
    this.callbacks.onProgress(`🎵 Playing [${this.currentTrackIdx + 1}/${this.setlist.tracks.length}]: ${track.name} (${track.genre} ${track.bpm}bpm)`);

    if (this.composer) {
      this.composer.setGenre(track.genre);
      this.composer.setBpm(track.bpm);
      this.composer.setKey(track.key, track.scale);
      this.composer.start();

      // Trigger appropriate section based on energy
      if (track.energy > 0.8) {
        this.composer.triggerDrop();
      } else if (track.energy > 0.5) {
        this.composer.triggerBuild();
      }
    }

    // Sync visuals to track
    this.coordinator.visualDJ.adjustIntensity(track.energy);
    this.coordinator.visualDJ.adjustSpeed(0.5 + track.energy * 0.5);

    // Schedule next track
    const trackDurationMs = (track.bars * 4 * 60 * 1000) / track.bpm;
    this.trackTimer = setTimeout(() => this.nextTrack(), trackDurationMs);
  }

  /** Handle transition between tracks (cut, mix, or filler). */
  private async handleTransition(type: string, from: DJTrack, to: DJTrack): Promise<void> {
    this.callbacks.onTransition(type, from, to);

    switch (type) {
      case 'cut':
        // Abrupt change — just switch immediately
        this.callbacks.onProgress(`✂️ CUT: ${from.name} → ${to.name}`);
        break;

      case 'mix':
        // Gradual blend — ramp BPM over 4 bars
        this.callbacks.onProgress(`🎚 MIX: ${from.name} → ${to.name} (ramping ${from.bpm}→${to.bpm}bpm)`);
        if (this.composer) {
          // ComposerEngine.setBpm already ramps over 2s, so we just call it
          this.composer.setBpm(to.bpm);
        }
        await this.delay(500);
        break;

      case 'filler':
        // Ambient recovery — slow down, play pad
        this.callbacks.onProgress(`🌫 FILLER: ambient recovery between ${from.name} → ${to.name}`);
        if (this.composer) {
          this.composer.setGenre('ambient');
          this.composer.setBpm(80);
        }
        await this.delay(2000);
        break;
    }
  }

  /** Skip to the next track manually. */
  async skip(): Promise<void> {
    if (this.trackTimer) {
      clearTimeout(this.trackTimer);
      this.trackTimer = null;
    }
    await this.nextTrack();
  }

  /** Get the current setlist. */
  get currentSetlist(): DJSetlist | null {
    return this.setlist;
  }

  /** Get the current track index. */
  get currentTrack(): number {
    return this.currentTrackIdx;
  }

  /** Is the DJ currently playing? */
  get isPlaying(): boolean {
    return this.running;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  dispose(): void {
    this.stop();
  }
}
